/* eslint-disable no-shadow */
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Cookies from 'universal-cookie';
import { applyContainerQuery } from 'react-container-query';
import {
  AuthContainer, authSteps, utils,
} from '@resy/ui-kitchen';
import parsePhone from '../../../../../ui-kitchen/src/utils/parsePhone';
import containerQueryMap from '../../maps/containerQueryMap';
import containerQueryProps from '../../types/containerQueryProps';
import { IDLE, asyncLoadingStatusTypes } from '../../../../../ui-kitchen/src/constants/asyncLoadingStatuses';
import { loadResyConfigThunk } from '../../../../../ui-kitchen/src/reducers';
import { selectIsAuthenticated } from '../../../../../ui-kitchen/src/reducers/authTokenSlice';
import { selectIsNonUS } from '../../../../../ui-kitchen/src/reducers/userSlice';
import { handleNavigateWithParams } from '../../../../../ui-kitchen/src/utils/locationUtils';
import { getURLSearchParams } from '../../../../../ui-kitchen/src/utils/searchParamUtils';
import { sanitizeTargetUrl } from '../../../../../ui-kitchen/src/utils/sanitizeTargetUrl';
import RouterNavigateContext from '../../contexts/RouterNavigate';

const { PHONE, LOGIN } = authSteps;

const { getQueryVariable } = utils;

const authContainerPropTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  AutomationMode: PropTypes.bool.isRequired,
  backPath: PropTypes.string.isRequired,
  callingCode: PropTypes.string,
  containerQuery: containerQueryProps,
  $routeParams: PropTypes.shape({
    authSuccessRedirectUrl: PropTypes.string,
  }),
  countryCodes: PropTypes.arrayOf(PropTypes.shape({})),
  configStatus: asyncLoadingStatusTypes.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  isNonUS: PropTypes.bool.isRequired,
  loadResyConfigThunk: PropTypes.func.isRequired,
  mobileFirstLogin: PropTypes.oneOf([0, 1]).isRequired,
  navVenueName: PropTypes.string.isRequired,
  deviceId: PropTypes.string.isRequired,
  sourceId: PropTypes.string,
  ResyAmexConfig: PropTypes.shape({
    baseRequestUrl: PropTypes.string.isRequired,
  }).isRequired,
};

class WrappedAuthContainer extends React.Component {
  static propTypes = authContainerPropTypes;

  static contextType = RouterNavigateContext;

  static defaultProps = {
    callingCode: '1',
    containerQuery: {},
    countryCodes: [{ iso3166: 'US', code: '1', country_name: 'United States' }],
    $routeParams: {},
    sourceId: undefined,
  }

  cookies = new Cookies();

  state = {
    defaultCountryCode: '1',
    mobile: '',
    deviceId: null,
    cookieDeltaPage: this.cookies.get('_deltaPage_'),
  }

  componentDidMount() {
    const {
      $routeParams, configStatus, isAuthenticated, loadResyConfigThunk, deviceId,
    } = this.props;

    // set cookie if allowed, removeEvent returned
    const [uuid, removeUuidCookieListener] = utils.getUuid(deviceId);
    this.removeUuidCookieListener = removeUuidCookieListener;
    this.setLocation();
    this.setDeviceId(uuid);

    if ($routeParams?.authSuccessRedirectUrl && isAuthenticated) {
      this.success();
    }

    if (configStatus === IDLE) {
      loadResyConfigThunk();
    }
  }

  componentWillUnmount() {
    this.removeUuidCookieListener();
  }

  removeUuidCookieListener = () => {};

  setLocation = async () => {
    const { callingCode } = this.props;
    const mobileNumber = getQueryVariable('phone') || null;
    let countryCode;
    let mobile;

    if (mobileNumber) {
      ({ countryCode, nationalFormat: mobile } = parsePhone(mobileNumber));
    } else {
      countryCode = callingCode;
      mobile = '';
    }

    this.setState({
      defaultCountryCode: countryCode,
      mobile,
    });
  }

  setDeviceId = (deviceId) => {
    this.setState({
      deviceId,
    });
  }

  success = () => {
    const { $routeParams } = this.props;
    window.parent.postMessage({
      event: 'authRedirect',
      redirectUrl: $routeParams.authSuccessRedirectUrl,
    }, '*');
  }

  path = (url) => {
    const navigate = this.context;
    if (/^http/.test(url)) {
      const sanitizedUrl = sanitizeTargetUrl(url);
      navigate(sanitizedUrl);
    } else {
      handleNavigateWithParams(url, undefined, navigate);
    }
  }

  onClose = () => {
    const { backPath } = this.props;
    const iframe = window.top !== window.self;
    /* sends the user back to the page they came from.
      i.e: https://pay.resy.com/
      ref: https://resy.atlassian.net/browse/CON-657 */
    const hasARefferedSource = !!document.referrer;
    const canGoBack = typeof window.history?.back === 'function';
    const canAssignLocation = typeof window.location?.assign === 'function';
    const rootUrl = window.location?.origin ? `${window.location.origin}/` : '/';

    if (iframe) {
      window.parent.postMessage({ event: 'close' }, '*');
    } else if (backPath) {
      this.path(backPath);
    } else if (hasARefferedSource && canGoBack) {
      window.history.back();
    } else if (canAssignLocation) {
      window.location.assign(rootUrl);
    } else {
      window.history.back();
    }
  }

  render() {
    const {
      $routeParams,
      Analytics: { event },
      AutomationMode,
      backPath,
      containerQuery,
      countryCodes,
      isNonUS,
      mobileFirstLogin,
      navVenueName,
      ResyAmexConfig,
      sourceId,
    } = this.props;
    const {
      deviceId,
      defaultCountryCode,
      mobile,
      cookieDeltaPage,
    } = this.state;
    const searchParams = getURLSearchParams();
    let next = searchParams.get('next') || undefined;

    let firstStep = mobileFirstLogin
      ? PHONE
      : LOGIN;

    if ($routeParams?.authSuccessRedirectUrl) {
      next = $routeParams?.authSuccessRedirectUrl;
      firstStep = PHONE;
    }

    let firstStepValue = firstStep;
    if (cookieDeltaPage === 'Y' && firstStep !== authSteps.SIGNUP) {
      firstStepValue = authSteps.LOGIN;
    }

    const $rootScopeNavShim = {
      backPath,
      venueName: navVenueName,
    };

    return (
      <AuthContainer
        AutomationMode={AutomationMode}
        close={this.onClose}
        containerQuery={containerQuery}
        countryCodes={countryCodes}
        defaultCountryCode={defaultCountryCode}
        deviceId={deviceId}
        email={getQueryVariable('email') || ''}
        event={event}
        firstName={getQueryVariable('firstName') || ''}
        firstStep={firstStepValue}
        grecaptcha={window.grecaptcha}
        isNonUS={isNonUS}
        lastName={getQueryVariable('lastName') || ''}
        marketingOptInChecked={false}
        mobile={mobile}
        nav={$rootScopeNavShim}
        next={next}
        path={this.path}
        ResyAmexConfig={ResyAmexConfig}
        sourceId={sourceId}
        context="widget"
      />
    );
  }
}

const mapStateToProps = (state) => {
  const { resyConfig, resyRouter } = state;
  const { backPath, navVenueName, sourceId } = resyRouter;
  return {
    isAuthenticated: selectIsAuthenticated(state),
    isNonUS: selectIsNonUS(state),
    configStatus: resyConfig.configStatus,
    countryCodes: resyConfig.config?.country_codes,
    mobileFirstLogin: resyRouter.mobileFirstLogin,
    backPath,
    navVenueName,
    deviceId: resyConfig.deviceId,
    sourceId,
  };
};

const mapDispatchToProps = {
  loadResyConfigThunk,
};

// Naming for AuthContainer-releated items is a mess, and making the best of it for now.
const ContainerQueryWrappedAuthContainer = applyContainerQuery(WrappedAuthContainer, containerQueryMap);

export { WrappedAuthContainer as WrappedComponent, authContainerPropTypes };
export default connect(mapStateToProps, mapDispatchToProps)(ContainerQueryWrappedAuthContainer);
